// §22 — the web package's tunables.
//
// §3's env table configures the *server*; these are the client-side halves of the same numbers,
// because a guest's version history lives entirely in OPFS (§7.5) and never sees the server.
// Keep the two in step: `server/src/config.ts` reads `HANGAR_VERSION_KEEP_*` with these defaults.

/** `HANGAR_VERSION_KEEP_COUNT` — never thin a history below this many versions (§7.5). */
export const VERSION_KEEP_COUNT = 50;

/** `HANGAR_VERSION_KEEP_DAYS` — never thin away a version younger than this (§7.5). */
export const VERSION_KEEP_DAYS = 30;

/** §7.5 — every version from the last day survives, however many there are. */
export const VERSION_KEEP_ALL_HOURS = 24;

/** §7.5 — between a day and a week old, one version per hour survives. Beyond, one per day. */
export const VERSION_HOURLY_DAYS = 7;

/** §7.6 — at 90 % of quota writes still succeed, but a warning fires once per session. */
export const QUOTA_WARN_RATIO = 0.9;

/** How often a write may trigger a `storage.estimate()`. It is not a free call. */
export const QUOTA_CHECK_INTERVAL_MS = 10_000;

/**
 * §11.1 — Peek reads the whole file into the window to show it, so it has a ceiling. Above these
 * it says so rather than locking the desktop up decoding a gigabyte.
 */
export const PEEK_MAX_TEXT_BYTES = 2_000_000;
export const PEEK_MAX_IMAGE_BYTES = 25_000_000;

/**
 * §7.7 — a drag out of Hangar has to stage the whole file in memory before `dragstart` runs,
 * because that handler cannot await. Above this the context menu's Download is the path.
 */
export const DRAG_OUT_MAX_BYTES = 64_000_000;

/** §18 F16 — the `BroadcastChannel` every tab of this origin talks over. */
export const TAB_CHANNEL = 'hangar-tabs';

/** §11.3 — Notepad's autosave debounce: 2 s after the last keystroke. */
export const NOTEPAD_AUTOSAVE_MS = 2_000;

export const MS_PER_HOUR = 3_600_000;
export const MS_PER_DAY = 86_400_000;

/**
 * §11.4 — how long a framed page has to fire `load` before the Browser calls it a refusal.
 *
 * There is no way to *ask* whether a cross-origin frame loaded — that is what the same-origin
 * policy is — and no content proxy to ask the server instead (D-015). So the detector is a clock,
 * and this is the one number in it: long enough that a slow site is not libelled, short enough
 * that a refusal is not mistaken for a hang.
 */
export const BROWSER_FRAME_TIMEOUT_MS = 3_000;

/** §11.7 — how long a slideshow rests on each picture. */
export const PHOTOS_SLIDESHOW_MS = 4_000;
/** §11.7 — the zoom range. Below the first, a photo is a dot; above the second, it is a pixel. */
export const PHOTOS_ZOOM_MIN = 0.1;
export const PHOTOS_ZOOM_MAX = 8;

/** §11.8 — the PDF reader's zoom range and step (D-170). */
export const PDF_ZOOM_MIN = 0.25;
export const PDF_ZOOM_MAX = 4;
export const PDF_ZOOM_STEP = 1.25;

/**
 * §11.8 — a glyph whose font declares no width. 500/1000 em is the conventional guess; a
 * monospaced face is 600 by construction. Only the spacing *within* one show-operation depends on
 * it — every operation's own origin comes from the text matrix, which is exact.
 */
export const PDF_DEFAULT_GLYPH_WIDTH = 500;
export const PDF_MONOSPACE_GLYPH_WIDTH = 600;

/** §11.8 — a form XObject may nest; this is where a self-referential one stops. */
export const PDF_FORM_DEPTH_LIMIT = 8;

/** §11.8 — a ceiling on a document the desktop will hold in memory at once. */
export const PDF_MAX_PAGES = 2_000;

/** §11.1 — Peek renders page one of a PDF, so it reads the file; above this it says so. */
export const PEEK_MAX_PDF_BYTES = 50_000_000;

/** §11.1 — the scale Peek shows that page at: a whole Letter sheet inside the overlay card. */
export const PEEK_PDF_ZOOM = 0.55;

/** §11.10 — the clock's own tick. One second: the coarsest that keeps a seconds display honest. */
export const CLOCK_TICK_MS = 1_000;

// ── §34.16 — Workbench (M56–M67). Every tunable the wave needs, named here so no build session
// invents one. Window defaults live beside them for the same reason.

/**
 * §34.2 — above this, the worker streams entry-by-entry instead of holding the archive whole.
 * Injectable in tests so the streaming path is exercised without a 64 MB fixture.
 */
export const WB_ARCHIVE_STREAM_OVER_MB = 64;

/** §34.2 — Peek lists an archive, but an archive can hold a hundred thousand entries. */
export const WB_ARCHIVE_PEEK_ENTRIES = 500;

export const ARCHIVE_WINDOW = { width: 720, height: 520, minWidth: 420, minHeight: 320 } as const;

// ── The rest of §34.16, landed up front for the four parallel lanes (D-512).
//
// §34.16's list is complete and binding, so there is nothing here for a build session to decide —
// which is exactly why it can all be declared before the apps that read it. The alternative is
// four lanes each appending to this file and colliding on the same closing lines, for values that
// were never in question.

/** §34.6 / D-496 — the §14 setting's default: above this a file is device-local, never synced. */
export const WB_MEDIA_SYNC_THRESHOLD_MB_DEFAULT = 256;

/** §34.5 — above this Diff refuses rather than diffing a file it cannot hold twice over. */
export const WB_DIFF_MAX_MB = 10;

/**
 * §34.11 — how far ahead recurrence is expanded for reminders.
 *
 * Expansion is windowed because an RRULE has no end: `FREQ=WEEKLY` with no `UNTIL` is an infinite
 * set, and the only two questions anyone asks of it are "what is on screen" and "what fires soon".
 * This is the second window.
 */
export const WB_CAL_REMINDER_HORIZON_DAYS = 90;

/**
 * §34.7 / §34.15 — the two third-party origins this wave adds, and the only two.
 *
 * `youtube-nocookie` is the embed host (`frame-src`); `i.ytimg.com` serves thumbnails (`img-src`).
 * Neither is in `connect-src`: the client never *talks* to YouTube, it frames it and shows its
 * pictures. The one thing that asks YouTube a question is the server's oEmbed route (§34.8), which
 * is why there is no third origin here.
 */
export const WB_YT_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';
export const WB_YT_THUMB = 'https://i.ytimg.com/vi/';

/**
 * §34.7 — "position is written to the queue item on every `infoDelivery` (**throttled 5 s**)".
 *
 * **Added by the M60/M61 lane** (D-530): §34.7 states the number and §34.16's list does not carry it,
 * and it is exactly the kind of value §22 says must not be a literal buried in a component —
 * `infoDelivery` arrives several times a second, so an unthrottled write would rewrite a synced VFS
 * file about four times a second for the length of a film.
 */
export const WB_MEDIA_POSITION_THROTTLE_MS = 5_000;

// §34.16 — window defaults per app. Min 420×320 throughout, which is the smallest frame any of
// these apps still says something true at.
const WB_MIN = { minWidth: 420, minHeight: 320 } as const;

/** §34.4 — Quick Notes: the app window, and the 280×240 pinned note (§5.6 restores it). */
export const NOTES_WINDOW = { width: 680, height: 480, ...WB_MIN } as const;
export const PINNED_NOTE_WINDOW = { width: 280, height: 240, minWidth: 200, minHeight: 160 } as const;
/**
 * §34.4 — the capture card. **Added by M58.2; §34.16 names Notes' two windows and not this third
 * one**, and it is a third window rather than a small library window: it is centred on the work
 * area, it holds one growing field, and it closes on `Enter`.
 *
 * Wider than it is tall because a note is a sentence, and short enough that "single-line growing to
 * multiline" is what the frame looks like rather than something the frame contradicts. The floor is
 * the card's own size, like the pinned note's and the mini-player's — the 420×320 minimum is for
 * windows you read in.
 */
export const NOTES_CAPTURE_WINDOW = { width: 520, height: 168, minWidth: 360, minHeight: 120 } as const;

/**
 * §34.3 — the Inspector's Performance tab (M57.4). **Not in §34.16**, which names the wave's window
 * sizes and route tunables and not this one; §34.3 states the 2 s sampling interval in prose, and
 * the sample count is the sparkline's own window.
 *
 * 2 s because it is what §34.3 says, and because a readout that samples faster than a person reads
 * costs frames to show noise. Thirty samples is a minute of history at that rate — long enough to
 * see a leak start, short enough that the sparkline still has a shape.
 */
export const INSPECTOR_SAMPLE_MS = 2000;
export const INSPECTOR_SPARK_SAMPLES = 30;

/** §34.5 — Diff, wide enough for two 80-column panes side by side. */
export const DIFF_WINDOW = { width: 960, height: 600, ...WB_MIN } as const;

/** §34.6 — Media, and the mini-player it collapses to. */
export const MEDIA_WINDOW = { width: 900, height: 620, ...WB_MIN } as const;
export const MEDIA_MINI_WINDOW = { width: 320, height: 88, minWidth: 320, minHeight: 88 } as const;

/** §34.9 — Voice Memos. */
export const VOICE_WINDOW = { width: 520, height: 420, ...WB_MIN } as const;

/** §34.10 — Snip. */
export const SNIP_WINDOW = { width: 860, height: 620, ...WB_MIN } as const;

/** §34.12 — Weather. */
export const WEATHER_WINDOW = { width: 680, height: 520, ...WB_MIN } as const;

// ── Lane C additions to §34.16 (M62–M65, D-560).
//
// §34.16's list is complete for the values it names, and these five are values §34.9/§34.10/§34.12
// state **in prose** rather than in the config table — a one-second chunk, two directory names, an
// hourly refresh, a geolocation deadline. They land here for §22's reason: the number a behaviour
// depends on belongs where somebody can find it, not inline in the module that happens to use it.
// Flagged at integration as an addition to §34.16 rather than a silent local constant.

/** §34.9 — the `MediaRecorder` timeslice: "1 s data chunks", which is also the flush cadence. */
export const VOICE_CHUNK_MS = 1_000;

/**
 * §34.9 / §34.10 — the two directories this wave writes into, relative to the user's own root.
 *
 * They live in config rather than in each app because **both halves need them**: the app builds a
 * path from one, and `host/capabilities.ts` grants reach to exactly that path (§9.5's `extraRoots`).
 * Putting the string in the app would mean `host/` importing an app module, which would drag the
 * app's engine into the boot path and undo D-511 for the sake of a constant.
 */
export const VOICE_MEMO_DIR = 'Documents/Voice Memos';
export const SNIP_DIR = 'Pictures/Screenshots';

/**
 * §34.12 — the age at which a forecast is called stale, **mirroring `server/src/config.ts`**.
 *
 * §34.16 lists this constant on the server side only, because that is where the TTL decides whether to
 * refetch. The client needs the same number for a different question — whether to draw "as of {age}
 * ago" — and keying it to the server's TTL rather than to a threshold of its own is what keeps the two
 * from disagreeing: the server refetches at exactly this age, so anything older means the last attempt
 * failed, which is precisely when §34.12 wants the age shown. Duplicated in the same spirit as the
 * `HANGAR_VERSION_KEEP_*` defaults at the top of this file, with the same obligation to stay in step.
 */
export const WB_WEATHER_TTL_MIN = 30;

/** §34.12 — "every 60 min while open". Refresh-on-open is the other half and needs no number. */
export const WB_WEATHER_REFRESH_MS = 60 * 60_000;

/**
 * §34.12 — how long **Use my location** waits before giving up.
 *
 * A deadline rather than none: `getCurrentPosition` with no timeout can sit unresolved forever on a
 * device whose GPS never gets a fix, and a button that never comes back is worse than one that says
 * it could not find you.
 */
export const WB_GEOLOCATION_TIMEOUT_MS = 10_000;
